import React, { useState, useEffect, useContext } from 'react';
import { supabase } from 'lib/supabase';
import AdminDashboardLayout, { KPIRefreshContext } from '../admin-dashboard-overview/components/AdminDashboardLayout';
import ClientsTable from './components/ClientsTable';
import SearchBar from './components/SearchBar';
import StatusFilter from './components/StatusFilter';
import Pagination from './components/Pagination';


const AdminClientsManagement = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [toast, setToast] = useState(null);
  const itemsPerPage = 25;
  const kpiRefresh = useContext(KPIRefreshContext);

  useEffect(() => {
    fetchClients();
  }, [currentPage, searchQuery, statusFilter]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        ?.from('users')
        ?.select('*', { count: 'exact' })
        ?.eq('role', 'cliente')
        ?.order('created_at', { ascending: false });

      if (searchQuery?.trim()) {
        query = query?.ilike('email', `%${searchQuery?.trim()}%`);
      }

      if (statusFilter !== 'all') {
        query = query?.eq('account_status', statusFilter);
      }

      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;
      query = query?.range(from, to);

      const { data, error: fetchError, count } = await query;
      if (fetchError) throw fetchError;

      const baseClients = data || [];

      // Fetch request counts and last activity for all clients in this page
      let enrichedClients = baseClients;
      if (baseClients?.length > 0) {
        const clientIds = baseClients?.map((c) => c?.id);

        const { data: requestStats } = await supabase
          ?.from('service_requests')
          ?.select('client_id, created_at')
          ?.in('client_id', clientIds);

        // Build a map: client_id -> { count, maxCreatedAt }
        const statsMap = {};
        (requestStats || [])?.forEach((r) => {
          if (!statsMap?.[r?.client_id]) {
            statsMap[r.client_id] = { count: 0, maxCreatedAt: null };
          }
          statsMap[r.client_id].count += 1;
          if (
            !statsMap?.[r?.client_id]?.maxCreatedAt ||
            new Date(r.created_at) > new Date(statsMap[r.client_id].maxCreatedAt)
          ) {
            statsMap[r.client_id].maxCreatedAt = r?.created_at;
          }
        });

        enrichedClients = baseClients?.map((c) => ({
          ...c,
          richieste_create: statsMap?.[c?.id]?.count || 0,
          ultima_attivita: statsMap?.[c?.id]?.maxCreatedAt || c?.updated_at || c?.created_at,
        }));
      }

      setClients(enrichedClients);
      setTotalCount(count || 0);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Errore nel caricamento dei clienti');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleAction = async (clientId, action) => {
    try {
      // Fresh auth state
      const { data: { user: adminUser } } = await supabase?.auth?.getUser();

      let newStatus;
      let successMessage = '';

      if (action === 'suspend') {
        newStatus = 'suspended';
        successMessage = 'Cliente sospeso con successo';
      } else if (action === 'ban') {
        newStatus = 'banned';
        successMessage = 'Cliente bannato con successo';
      } else if (action === 'reactivate') {
        newStatus = 'active';
        successMessage = 'Cliente riattivato con successo';
      }

      // Fetch current client data before update
      const { data: currentData } = await supabase
        ?.from('users')
        ?.select('email, account_status')
        ?.eq('id', clientId)
        ?.single();

      const oldStatus = currentData?.account_status;

      const { error: updateError } = await supabase
        ?.from('users')
        ?.update({ account_status: newStatus })
        ?.eq('id', clientId);

      if (updateError) throw updateError;

      // Log via logAdminAction edge function
      try {
        await supabase?.functions?.invoke('logAdminAction', {
          body: {
            action: 'CLIENT_STATUS_CHANGED',
            entity_type: 'client',
            entity_id: clientId,
            old_value: { account_status: oldStatus },
            new_value: { account_status: newStatus },
          },
        });
      } catch (logErr) {
        console.error('Audit log error (non-blocking):', logErr);
      }

      // Send status email (best effort)
      if (oldStatus !== newStatus && currentData?.email) {
        try {
          await supabase?.functions?.invoke('send-professional-status-email', {
            body: {
              email: currentData?.email,
              name: 'Cliente',
              statusType: 'account_status',
              newStatus,
            },
          });
        } catch (emailErr) {
          console.error('Email exception (non-blocking):', emailErr);
        }
      }

      setToast({ type: 'success', message: successMessage });
      await fetchClients();

      if (kpiRefresh?.refreshKPIs) {
        await kpiRefresh?.refreshKPIs();
      }
    } catch (err) {
      console.error('Error updating client status:', err);
      setToast({ type: 'error', message: 'Errore durante l\'aggiornamento dello stato' });
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <AdminDashboardLayout>
      <div className="space-y-6">
        {/* Toast Notification */}
        {toast && (
          <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 animate-slide-in ${
            toast?.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}>
            <span>{toast?.message}</span>
          </div>
        )}
        {/* Page Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestione Clienti</h1>
          <p className="text-gray-600 mt-2">Visualizza e gestisci tutti i clienti registrati</p>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SearchBar onSearch={handleSearch} placeholder="Cerca per email..." />
            <StatusFilter onFilter={handleStatusFilter} currentFilter={statusFilter} />
          </div>
        </div>

        {/* Results Summary */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Totale: <span className="font-semibold">{totalCount}</span> clienti
          </p>
          <p className="text-sm text-gray-600">
            Pagina {currentPage} di {totalPages || 1}
          </p>
        </div>

        {/* Table */}
        {loading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-600">Caricamento...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        ) : (
          <ClientsTable clients={clients} onAction={handleAction} />
        )}

        {/* Pagination */}
        {!loading && !error && totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    </AdminDashboardLayout>
  );
};

export default AdminClientsManagement;